/**
 * Who is giving feedback on a branded line sheet (KP-4572), as far as this
 * browser knows.
 *
 * - The visitor key: a random UUID v4 made once per browser. The server keeps
 *   only its hash; whoever holds it reads and writes that self-identified
 *   viewer's feedback, so typing someone else's email never reveals theirs.
 * - The remembered identity: the email and store name typed last, reused on
 *   any sheet so a buyer identifies once.
 * - The "Not you?" flag, per sheet: this browser stops speaking as the
 *   retailer the email link names.
 *
 * Storage can be blocked (private windows) or full. Every read and write is
 * guarded and falls back to memory, so the values hold for this visit only.
 */

import { cleanLine } from './feedbackText'

export const VISITOR_KEY = 'kp-ls-visitor'
export const IDENTITY_KEY = 'kp-ls-identity'
const NOT_ME_PREFIX = 'kp-ls-not-me:'

export const EMAIL_MAX = 254
export const STORE_NAME_MAX = 120

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

// This visit's values, for when storage refuses them.
let visit = new Map()

/** Test seam: forget this visit's in-memory values. */
export function forgetVisitForTests() {
  visit = new Map()
}

function read(key) {
  try {
    const value = window.localStorage.getItem(key)
    if (value !== null) return value
  } catch (e) {
    // Blocked: fall through to this visit's copy.
  }
  return visit.has(key) ? visit.get(key) : null
}

function write(key, value) {
  visit.set(key, value)
  try {
    window.localStorage.setItem(key, value)
  } catch (e) {
    // Blocked or full: the value lives for this visit only.
  }
}

const hex = bytes => Array.from(bytes, b => (b % 256).toString(16).padStart(2, '0')).join('')

/** A fresh UUID v4 from the given crypto (randomUUID, else getRandomValues). */
export function newVisitorKey(cryptoImpl = typeof window !== 'undefined' ? window.crypto : undefined) {
  if (cryptoImpl && typeof cryptoImpl.randomUUID === 'function') return cryptoImpl.randomUUID()
  const raw = new Uint8Array(16)
  const bytes = cryptoImpl && typeof cryptoImpl.getRandomValues === 'function'
    ? cryptoImpl.getRandomValues(raw)
    : raw.map(() => Math.floor(Math.random() * 256))
  const b = Uint8Array.from(bytes)
  // Version 4 in the high nibble of byte 6, variant 10xx in byte 8.
  b[6] = 0x40 + (b[6] % 16)
  b[8] = 0x80 + (b[8] % 64)
  const h = hex(b)
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

/** This browser's visitor key, made on first use. */
export function visitorKey() {
  const stored = read(VISITOR_KEY)
  if (stored && UUID_V4.test(stored)) return stored.toLowerCase()
  const key = newVisitorKey()
  write(VISITOR_KEY, key)
  return key
}

/** True when email has the shape the server accepts. */
export function isEmailLike(raw) {
  const email = String(raw || '').trim()
  if (email.length < 3 || email.length > EMAIL_MAX || /\s/.test(email)) return false
  const parts = email.split('@')
  if (parts.length !== 2 || !parts[0]) return false
  const domain = parts[1]
  const dot = domain.indexOf('.')
  return dot > 0 && dot < domain.length - 1
}

/** A new identity in the shape the server stores: email lowercased, store name cleaned. */
export function cleanIdentity({ email = '', store_name: storeName = '' } = {}) {
  return {
    email: String(email || '').trim().toLowerCase(),
    store_name: cleanLine(storeName, STORE_NAME_MAX),
  }
}

/** The identity typed last in this browser, or null. */
export function storedIdentity() {
  try {
    const parsed = JSON.parse(read(IDENTITY_KEY) || 'null')
    if (!parsed || !isEmailLike(parsed.email)) return null
    return cleanIdentity(parsed)
  } catch (e) {
    return null
  }
}

/** Remembers the identity for every sheet in this browser. */
export function saveIdentity(identity) {
  write(IDENTITY_KEY, JSON.stringify(cleanIdentity(identity)))
}

/** True once "Not you?" was chosen on this sheet in this browser. */
export function ignoresTrackedLink(token) {
  return read(`${NOT_ME_PREFIX}${token}`) === '1'
}

export function setIgnoresTrackedLink(token) {
  write(`${NOT_ME_PREFIX}${token}`, '1')
}
